import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import type { Role } from './api';
import { useRoleMutations } from './useRoleMutations';

interface Args {
  mode: 'create' | 'edit' | 'empty';
  role: Role | null;
  onSaved: (saved: Role) => void;
}

export interface SaveInput {
  name: string;
  slug: string;
  description: string | null;
  desired: Set<string>;
  baseline: Set<string>;
}

export function useRoleEditorSave({ mode, role, onSaved }: Args) {
  const { t } = useTranslation('rbac');
  const { create, update } = useRoleMutations();
  const [savedAt, setSavedAt] = useState(0);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const save = (input: SaveInput): void => {
    setErrorKey(null);
    const onError = (err: unknown): void => {
      const key = parseApiError(err).i18nKey;
      setErrorKey(key);
      toast.error(t(key, key));
    };
    const onSuccess = (saved: Role): void => {
      setSavedAt(Date.now());
      onSaved(saved);
      toast.success(
        t(mode === 'create' ? 'toasts.role_created' : 'toasts.role_updated'),
      );
    };
    if (mode === 'create') {
      create.mutate(
        {
          name: input.name,
          slug: input.slug,
          description: input.description,
          desired: input.desired,
        },
        { onSuccess, onError },
      );
      return;
    }
    if (!role) return;
    update.mutate(
      {
        role,
        name: input.name,
        slug: input.slug,
        description: input.description,
        desired: input.desired,
        baseline: input.baseline,
      },
      { onSuccess, onError },
    );
  };

  return {
    save,
    isPending: create.isPending || update.isPending,
    savedAt,
    errorKey,
    setErrorKey,
  };
}
